import { RenderFormat, ViewerOpenContextKind, type ContentViewerSession } from '@/content-viewer/store/schemas'
import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import { buildDeletedUserFileViewerSession } from '@/user_files/files/utils/build-deleted-user-file-viewer-session'
import { buildUserFileRendererConfig } from '@/user_files/files/utils/build-user-file-renderer-config'
import { buildUserFileViewerSession } from '@/user_files/files/utils/build-user-file-viewer-session'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { type MedChronReference } from '@/medical-chronology/references/store/schemas'
import { type MedChronSourceRef } from './schemas'

type BuildMedChronViewerSessionArgs = {
  source: MedChronSourceRef
  reference?: MedChronReference
  userFile?: UserFileRead | null
  highlights?: PaxtonHighlightArea[]
  startingPage?: number
}

/** Resolves a user-files med-chron source into the normalized content-viewer session. */
export function buildMedChronViewerSession(args: BuildMedChronViewerSessionArgs): ContentViewerSession | null {
  const { source, reference, userFile, highlights, startingPage } = args

  const context = reference
    ? {
        kind: ViewerOpenContextKind.MED_CHRON_REFERENCE as const,
        referenceId: reference.id,
        sourceId: source.source_id,
      }
    : {
        kind: ViewerOpenContextKind.MED_CHRON_SOURCE as const,
        sourceId: source.source_id,
      }

  // The record is gone: open anyway so the pane renders the deleted state. See
  // buildDeletedUserFileViewerSession for what an absent record does and does not prove.
  if (!userFile) {
    return buildDeletedUserFileViewerSession({ userFileId: source.source_id, title: source.display_name, context })
  }

  const baseRenderer = buildUserFileRendererConfig(userFile)
  if (!baseRenderer) return null

  // Med source highlighting is PDF-only today. Non-PDF sources need a separate
  // presentation primitive before highlights or startingPage can apply.
  const renderer =
    baseRenderer.format === RenderFormat.PDF
      ? {
          ...baseRenderer,
          ...(highlights ? { highlights } : {}),
          ...(startingPage !== undefined ? { startingPage } : {}),
        }
      : baseRenderer

  return buildUserFileViewerSession({ userFile, renderer, context })
}
